import React from 'react'

const CHROME_STORE_URL = 'https://chromewebstore.google.com/detail/nowpilot-ai-copilot-for-f/fbcdieoakcimbcaeblflmolklimhlnii'

declare global {
  interface Window {
    gtag: (...args: any[]) => void
  }
}

function BrowserMockup() {
  return (
    <div className="w-full max-w-2xl mx-auto float-gentle">
      <div className="bg-white rounded-xl shadow-xl border border-ink/10 overflow-hidden">
        <div className="flex items-center gap-2 px-4 py-2.5 bg-paper/60 border-b border-ink/10">
          <div className="flex gap-1.5">
            <div className="w-3 h-3 rounded-full bg-red-400" />
            <div className="w-3 h-3 rounded-full bg-yellow-400" />
            <div className="w-3 h-3 rounded-full bg-green-400" />
          </div>
          <div className="flex gap-0.5 ml-3 text-xs">
            <div className="px-3 py-1 bg-white rounded-t border border-b-0 border-ink/10 text-ink-soft flex items-center gap-1.5">
              <svg className="w-3 h-3 text-brand" viewBox="0 0 128 128"><g transform="translate(64,64) scale(7)"><circle cx="0" cy="0" r="8" fill="none" stroke="currentColor" strokeWidth="1.75"/><polygon points="0,-4.5 3,3 0,1.5 -3,3" fill="currentColor"/></g></svg>
              NowPilot
            </div>
            <div className="px-3 py-1 bg-paper/40 rounded-t border border-b-0 border-ink/10 text-ink font-medium flex items-center gap-1.5">
              <svg className="w-3 h-3" viewBox="0 0 24 18"><rect width="24" height="18" rx="4" fill="#FF0000"/><polygon points="10,3.5 10,14.5 18,9" fill="white"/></svg>
              YouTube
            </div>
          </div>
        </div>

        <div className="relative bg-paper/40 p-4" style={{ minHeight: '280px' }}>
          <div className="flex gap-3 mb-3">
            <div className="w-full">
              <div className="flex items-center gap-2 mb-3">
                <div className="h-5 bg-brand-soft rounded-full px-3 flex items-center text-xs text-brand-deep font-medium">All</div>
                <div className="h-5 bg-sand rounded-full px-3 flex items-center text-xs text-ink-soft">Music</div>
                <div className="h-5 bg-sand rounded-full px-3 flex items-center text-xs text-ink-soft">Gaming</div>
                <div className="h-5 bg-sand rounded-full px-3 flex items-center text-xs text-ink-soft">Podcasts</div>
              </div>
              <div className="grid grid-cols-3 gap-3">
                <div>
                  <div className="aspect-video bg-gradient-to-br from-teal-200 to-teal-300 rounded-lg mb-1.5" />
                  <div className="h-2.5 bg-ink/10 rounded w-full mb-1" />
                  <div className="h-2 bg-ink/8 rounded w-2/3" />
                </div>
                <div>
                  <div className="aspect-video bg-gradient-to-br from-blue-200 to-blue-300 rounded-lg mb-1.5" />
                  <div className="h-2.5 bg-ink/10 rounded w-full mb-1" />
                  <div className="h-2 bg-ink/8 rounded w-3/4" />
                </div>
                <div>
                  <div className="aspect-video bg-gradient-to-br from-amber-100 to-amber-200 rounded-lg mb-1.5" />
                  <div className="h-2.5 bg-ink/10 rounded w-full mb-1" />
                  <div className="h-2 bg-ink/8 rounded w-1/2" />
                </div>
              </div>
            </div>
          </div>

          <div className="absolute bottom-4 right-4 w-72 bg-white rounded-xl shadow-xl border border-ink/10 overflow-hidden">
            <div className="flex items-center gap-2 px-4 py-2.5 border-b border-ink/10">
              <svg className="w-4 h-4 text-brand" viewBox="0 0 128 128"><g transform="translate(64,64) scale(7)"><circle cx="0" cy="0" r="8" fill="none" stroke="currentColor" strokeWidth="1.75"/><polygon points="0,-4.5 3,3 0,1.5 -3,3" fill="currentColor"/></g></svg>
              <span className="text-sm font-semibold text-ink">NowPilot</span>
            </div>
            <div className="p-3">
              <div className="bg-paper/60 rounded-lg p-3 text-sm text-ink-mute leading-relaxed">
                Hey — you've been on YouTube for a bit. Want help getting back to what you were working on?
              </div>
            </div>
            <div className="flex items-center gap-2 px-3 pb-3">
              <input
                type="text"
                placeholder="Type a message..."
                className="flex-1 text-xs px-3 py-2 bg-paper/60 border border-ink/10 rounded-lg"
                readOnly
                tabIndex={-1}
              />
              <div className="w-8 h-8 bg-brand rounded-lg flex items-center justify-center flex-shrink-0">
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="white" strokeWidth="2.5" strokeLinecap="round"><path d="M22 2L11 13"/><path d="M22 2l-7 20-4-9-9-4z"/></svg>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export function HeroSection() {
  const handleChromeStoreClick = () => {
    if (typeof window.gtag !== 'undefined') {
      window.gtag('event', 'chrome_store_click', {
        event_category: 'engagement',
        event_label: 'hero_cta',
      })
    }
    window.open(CHROME_STORE_URL, '_blank', 'noopener')
  }

  return (
    <section id="top" className="relative pt-20 pb-24 md:pt-28 md:pb-32 bg-paper overflow-hidden">
      <div
        aria-hidden
        className="pointer-events-none absolute inset-x-0 top-0 mx-auto h-[520px] max-w-3xl"
        style={{
          background:
            'radial-gradient(closest-side, rgba(78,105,181,0.16), rgba(78,105,181,0) 70%)',
        }}
      />

      <div className="relative container mx-auto px-4 max-w-5xl">
        <div className="text-center mb-16">
          <div className="flex items-center justify-center gap-3 mb-8 fade-up" style={{ animationDelay: '0s' }}>
            <span className="hidden sm:block h-px w-10 bg-ink/15" />
            <span className="eyebrow">For ADHD Brains · Chrome Extension</span>
            <span className="hidden sm:block h-px w-10 bg-ink/15" />
          </div>

          <h1
            className="font-display text-[clamp(2.75rem,8vw,6rem)] leading-[1.02] tracking-tightest text-ink mb-7 fade-up"
            style={{ animationDelay: '0.12s' }}
          >
            AI Copilot for{' '}
            <span className="relative inline-block italic">
              Focus
              <svg
                aria-hidden
                viewBox="0 0 220 14"
                className="absolute -bottom-2 left-0 w-full h-3"
                preserveAspectRatio="none"
              >
                <path
                  d="M 6 9 Q 60 2, 120 7 T 214 6"
                  stroke="#4E69B5"
                  strokeWidth="2.5"
                  fill="none"
                  strokeLinecap="round"
                  className="underline-draw"
                />
              </svg>
            </span>
          </h1>

          <p
            className="text-lg md:text-xl text-ink-mute mb-10 max-w-xl mx-auto leading-relaxed fade-up"
            style={{ animationDelay: '0.24s' }}
          >
            A Chrome extension that notices when you drift — and helps you
            get back on track without shame.
          </p>

          <div className="fade-up" style={{ animationDelay: '0.36s' }}>
            <button
              onClick={handleChromeStoreClick}
              className="group inline-flex items-center gap-2 px-8 py-4 bg-brand text-white text-base font-medium rounded-full hover:bg-brand-deep hover:-translate-y-0.5 transition-all shadow-md hover:shadow-lg"
            >
              Add to Chrome — it's free
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                className="transition-transform group-hover:translate-x-0.5"
              >
                <path
                  d="M3 8h10m0 0L9 4m4 4L9 12"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                />
              </svg>
            </button>
            <p className="mt-4 text-[10.5px] font-mono uppercase tracking-[0.16em] text-ink-soft">
              No account · Works instantly
            </p>
          </div>
        </div>

        <div className="fade-up" style={{ animationDelay: '0.5s' }}>
          <BrowserMockup />
        </div>
      </div>
    </section>
  )
}
